import React, { useEffect, useState, useMemo } from 'react';
import { postListarLotesLab } from '../../../api/common';
import { Table, Container, Row, Col, Form, Button, Badge, Pagination, Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEdit, FaEye, FaBoxes, FaClock, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

// Listado de lotes con filtros, paginado y resumen por estado.
// Prop `refreshKey` (spec lotes-cola-lotes-refresh-reactivo): cuando cambia,
// re-consulta los lotes (bumpeado desde Settinglistingreso tras crear lote en cola).
const LotesList = ({ refreshKey = 0 }) => {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [onlyMine, setOnlyMine] = useState(false);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const res = await postListarLotesLab({});
        const payload = res && res.data ? (res.data.data ?? res.data) : res;
        setLotes(Array.isArray(payload) ? payload : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [refreshKey]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = lotes.slice();
    if (s) list = list.filter(l => (l.numeroLote ?? '').toLowerCase().includes(s) || (l.responsable?.nombre ?? '').toLowerCase().includes(s));
    // onlyMine filter will be applied by user UI later (requires userData). Keep here for future extension.
    return list;
  }, [lotes, search, onlyMine]);

  const counts = useMemo(() => {
    const map = { pendiente:0, abierto:0, en_progreso:0, cerrado:0 };
    lotes.forEach(l => map[l.estado] = (map[l.estado] || 0) + 1);
    return map;
  }, [lotes]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage-1)*pageSize, currentPage*pageSize);

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="align-items-center mb-4">
        <Col>
          <h3 className="mb-0">
            <FaBoxes className="me-2" />
            Lotes de Laboratorio
          </h3>
        </Col>
        <Col className="text-end">
          <Button as={Link} to="/mediciones/lotes/crear" variant="primary" size="lg">
            + Nuevo lote
          </Button>
        </Col>
      </Row>

      {/* Indicadores profesionales en Cards */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="shadow-sm border-0" style={{borderRadius: '12px', background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)'}}>
            <Card.Body className="text-center py-4">
              <FaClock size={32} className="mb-2" style={{color: '#d63031'}} />
              <h2 className="mb-1 fw-bold">{counts.pendiente}</h2>
              <p className="mb-0 text-muted">Pendientes</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0" style={{borderRadius: '12px', background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'}}>
            <Card.Body className="text-center py-4 text-white">
              <FaBoxes size={32} className="mb-2" />
              <h2 className="mb-1 fw-bold">{counts.abierto}</h2>
              <p className="mb-0">Abiertos</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0" style={{borderRadius: '12px', background: 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)'}}>
            <Card.Body className="text-center py-4 text-white">
              <FaClock size={32} className="mb-2" />
              <h2 className="mb-1 fw-bold">{counts.en_progreso}</h2>
              <p className="mb-0">En progreso</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0" style={{borderRadius: '12px', background: 'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)'}}>
            <Card.Body className="text-center py-4">
              <FaCheckCircle size={32} className="mb-2" style={{color: '#00b894'}} />
              <h2 className="mb-1 fw-bold">{counts.cerrado}</h2>
              <p className="mb-0 text-muted">Cerrados</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="shadow-sm mb-4 w-100" style={{borderRadius: '12px'}}>
        <Card.Body className='w-100'>
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Control 
                placeholder="🔍 Buscar por número de lote o responsable..." 
                value={search} 
                onChange={(e)=>{setSearch(e.target.value); setPage(1);}}
                size="lg"
                style={{borderRadius: '8px'}}
              />
            </Col>
            <Col md={6} className="text-end">
              <span className="text-muted">Total: <strong>{filtered.length}</strong> lote(s)</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla profesional */}
      <Card className="shadow-sm w-100" style={{borderRadius: '12px'}}>
        <Card.Body className="p-0 w-100">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Cargando lotes...</p>
            </div>
          ) : pageItems.length === 0 ? (
            <div className="text-center py-5">
              <FaTimesCircle size={48} className="text-muted mb-3" />
              <p className="text-muted">No se encontraron lotes</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead style={{backgroundColor: '#f8f9fa'}}>
                <tr>
                  <th className="py-3">Nº Lote</th>
                  <th className="py-3">Responsable</th>
                  <th className="py-3">Cantidad equipos</th>
                  <th className="py-3">Estado</th>
                  <th className="py-3 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map(l => (
                  <tr key={l._id}>
                    <td className="align-middle">
                      <strong>{l._consecutivo ?? l.numeroLote}</strong>
                    </td>
                    <td className="align-middle">
                      {l.responsable ? `${l.responsable.nombre} ${l.responsable.apellido || ''}` : '-'}
                    </td>
                    <td className="align-middle">
                      <Badge bg="secondary" pill>{(l.equipos || []).length}</Badge>
                    </td>
                    <td className="align-middle">
                      <Badge 
                        bg={l.estado==='pendiente'?'warning': l.estado==='abierto'?'success': l.estado==='en_progreso'?'info':'secondary'}
                        style={{borderRadius: '20px', padding: '6px 12px'}}
                      >
                        {l.estado}
                      </Badge>
                    </td>
                    <td className="align-middle text-center">
                      <Button 
                        size="sm" 
                        as={Link} 
                        to={`/mediciones/lotes/${l._id}`} 
                        variant="outline-primary" 
                        className="me-2"
                        style={{borderRadius: '8px'}}
                      >
                        <FaEye className="me-1" /> Ver
                      </Button>
                      <Button 
                        size="sm" 
                        as={Link} 
                        to={`/mediciones/lotes/${l._id}/editar`} 
                        variant="outline-secondary"
                        style={{borderRadius: '8px'}}
                      >
                        <FaEdit className="me-1" /> Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Paginación profesional */}
      <Row className="mt-4 align-items-center">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted">Elementos por página</Form.Label>
            <Form.Select 
              value={pageSize} 
              onChange={(e)=>{setPageSize(Number(e.target.value)); setPage(1);}} 
              style={{width:120, borderRadius: '8px'}}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={9} className="text-end">
          <Pagination className="justify-content-end mb-0">
            <Pagination.First onClick={()=>setPage(1)} disabled={currentPage===1} />
            <Pagination.Prev onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={currentPage===1} />
            <Pagination.Item active>{currentPage} de {totalPages}</Pagination.Item>
            <Pagination.Next onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages} />
            <Pagination.Last onClick={()=>setPage(totalPages)} disabled={currentPage===totalPages} />
          </Pagination>
        </Col>
      </Row>
    </Container>
  );
};

export default LotesList;
